// =============================================================================
// RepeatFlagPanel.jsx — the gold "Possible repeat" panel under a fee or device
// =============================================================================
// Possible-repeat flag, Phase A (db-210). Shown to Zephyr staff only, under a fee row
// or the device panel that request_repeat_matches() matched against earlier requests
// for what looks like the same child. Read-only guide: it never blocks a decision.
//
// Earlier-request links open in a NEW TAB on purpose — an in-app jump would leave this
// request and discard any decisions staged but not yet confirmed.
//
// Props:
//   repeat - one ItemRepeat from src/services/repeatCheck.ts (groupRepeatMatches)
//   review - optional { author, text, date } — the latest Repeat check note, once reviewed
//   months - the look-back window in months (System Setup; default 12)
// =============================================================================
import { formatRef, money } from "../../utils/helpers";

const GOLD = "#FFB300";
const GOLD_BG = "#FFF8E1";
const BROWN = "#8A4B00"; // 4.5:1+ on the gold background
const BROWN_DARK = "#5D3A00";

const DECISION_COLOUR = {
  Approved: "#2E7D32",
  Declined: "#C62828",
  "Needs Info": "#8A4B00",
  Pending: "#455A64",
};

function shortDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

function WarnIcon({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={BROWN}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12" y2="17" />
    </svg>
  );
}

/** Small gold "POSSIBLE REPEAT" chip (solid = strong match, dashed = weaker). */
export function RepeatChip({ strong }) {
  return (
    <span
      style={{
        display: "inline-block",
        fontSize: 11,
        fontWeight: 700,
        padding: "2px 7px",
        borderRadius: 10,
        background: GOLD_BG,
        color: BROWN,
        border: `1px ${strong ? "solid" : "dashed"} ${GOLD}`,
        whiteSpace: "nowrap",
      }}
    >
      {strong ? "POSSIBLE REPEAT · strong" : "POSSIBLE REPEAT"}
    </span>
  );
}

/**
 * The small "⚠ REPEAT" chip for the Responders Task List and All Requests (Phase B, db-211).
 * Solid border = strong match (Client Reference matches too), dashed = weaker.
 */
export function RepeatListChip({ flag }) {
  if (!flag) return null;
  const n = flag.flaggedItems || 0;
  return (
    <span
      data-testid="repeat-list-chip"
      title={`${n} item${n === 1 ? "" : "s"} look like a repeat for the same child${flag.strong ? " (strong match)" : ""}`}
      style={{
        display: "inline-block",
        alignSelf: "flex-start",
        marginTop: 3,
        fontSize: 10,
        fontWeight: 700,
        padding: "1px 6px",
        borderRadius: 9,
        background: GOLD_BG,
        color: BROWN,
        border: `1px ${flag.strong ? "solid" : "dashed"} ${GOLD}`,
        whiteSpace: "nowrap",
      }}
    >
      {flag.strong ? "⚠ REPEAT · strong" : "⚠ REPEAT"}
    </span>
  );
}

export function RepeatFlagPanel({ repeat, review, months = 12 }) {
  if (!repeat?.earlier || repeat.earlier.length === 0) return null;
  const n = repeat.earlier.length;
  const what = repeat.itemKind === "device" ? "device" : `${repeat.itemType} fee`;
  const th = { padding: "6px 10px", fontWeight: 700, color: BROWN_DARK, textAlign: "left" };
  const td = { padding: "6px 10px", borderTop: "1px solid #F0D48A" };
  return (
    <section
      aria-label="Possible repeat"
      data-testid="repeat-flag-panel"
      style={{
        background: GOLD_BG,
        border: `2px solid ${GOLD}`,
        borderRadius: 6,
        padding: "10px 12px",
        marginBottom: 8,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <WarnIcon size={18} />
        <RepeatChip strong={repeat.strong} />
        <span style={{ fontSize: 13, fontWeight: 700, color: BROWN }}>
          {`A similar ${what} was requested for a child like this ${n} time${n === 1 ? "" : "s"} in the last ${months} months`}
        </span>
      </div>
      <div style={{ fontSize: 12, color: BROWN_DARK }}>
        Matched on: same DFV Service · gender · year level · school
        {repeat.strong ? (
          <strong> · same Client Reference (strong match)</strong>
        ) : (
          " · different Client Reference"
        )}
      </div>
      <table
        style={{
          borderCollapse: "collapse",
          fontSize: 13,
          background: "#fff",
          border: "1px solid #F0D48A",
        }}
      >
        <thead>
          <tr style={{ background: "#FFF3CD" }}>
            <th style={th}>Request</th>
            <th style={th}>Date</th>
            <th style={th}>Description</th>
            <th style={th}>Amount</th>
            <th style={th}>Decision</th>
          </tr>
        </thead>
        <tbody>
          {repeat.earlier.map(function (e) {
            return (
              <tr key={`${e.requestId}-${e.createdAt}-${e.desc ?? ""}`}>
                <td style={td}>
                  <a
                    href={`/requests/${e.requestId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Opens in a new tab"
                    style={{ color: "#0D47A1" }}
                  >
                    {`${formatRef(e.refNo)} ↗`}
                  </a>
                </td>
                <td style={td}>{shortDate(e.createdAt)}</td>
                <td style={td}>{e.desc || "—"}</td>
                <td style={td}>{money(e.amount)}</td>
                <td
                  style={{
                    ...td,
                    fontWeight: 700,
                    color: DECISION_COLOUR[e.decision] || "#455A64",
                  }}
                >
                  {e.decision}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ fontSize: 12, color: BROWN_DARK }}>
        A guide only — children aren't named, so a match may be a different child (e.g. a sibling in
        the same year). Check the earlier request before deciding. Approved so far:{" "}
        <strong>{money(repeat.approvedTotal)}</strong>
      </div>
      {review?.text && (
        <div
          style={{
            background: "#E8F5E9",
            border: "1px solid #81C784",
            borderRadius: 6,
            padding: "8px 10px",
            fontSize: 13,
          }}
        >
          <div style={{ fontWeight: 700, color: "#1B5E20" }}>
            {`Repeat check — reviewed by ${review.author || "Zephyr"}${review.date ? `, ${shortDate(review.date)}` : ""}`}
          </div>
          <div style={{ whiteSpace: "pre-wrap", color: "#1A2E2B" }}>{review.text}</div>
        </div>
      )}
    </section>
  );
}
